import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard,
  FolderOpen,
  Calendar,
  Star,
  Settings,
  LogOut,
  Menu,
  X,
  Briefcase,
  Clock,
  Users,
  UserCircle,
  CreditCard,
  Mail,
  DatabaseBackup,
  Gift
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import NotificationBadge from '@/components/NotificationBadge';

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems = [
    { href: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { href: '/admin/progetti', icon: FolderOpen, label: 'Progetti' },
    { href: '/admin/prenotazioni', icon: Calendar, label: 'Prenotazioni' },
    { href: '/admin/pagamenti', icon: CreditCard, label: 'Pagamenti' },
    { href: '/admin/disponibilita', icon: Clock, label: 'Disponibilità' },
    { href: '/admin/servizi', icon: Briefcase, label: 'Servizi' },
    { href: '/admin/recensioni', icon: Star, label: 'Recensioni' },
    { href: '/admin/leads', icon: Users, label: 'Leads' },
    { href: '/admin/email-logs', icon: Mail, label: 'Email Logs' },
    { href: '/admin/buoni-regalo', icon: Gift, label: 'Buoni Regalo' },
    { href: '/admin/backup', icon: DatabaseBackup, label: 'Backup' },
    { href: '/admin/impostazioni', icon: Settings, label: 'Impostazioni' },
    { href: '/admin/account', icon: UserCircle, label: 'Account' },
  ];

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background border-b h-16 flex items-center justify-between px-4">
        <div className="flex items-center">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2"
            data-testid="admin-mobile-menu-btn"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <span className="ml-4 font-heading font-semibold">Admin Panel</span>
        </div>
        <NotificationBadge />
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-full w-64 bg-background border-r transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center px-6 border-b">
            <Link to="/admin" className="font-heading text-lg font-semibold" data-testid="admin-logo">
              Andrea Caruso
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
            {menuItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive(item.href)
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t">
            <div className="text-sm text-muted-foreground mb-3">
              {user?.email}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="w-full"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} className="mr-2" />
              Esci
            </Button>
          </div>

          {/* Back to Site */}
          <div className="p-4 border-t">
            <Link
              to="/"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="admin-back-to-site"
            >
              ← Torna al sito
            </Link>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
